import { createSignal, createResource, createMemo, createEffect, on, For, Show, onMount, onCleanup } from "solid-js";
import { useNavigate, useSearchParams } from "@solidjs/router";
import { useApi, isOk } from "../api";
import { useTranslation } from "../hooks/useTranslation";
import { useToast } from "../components/MessageBox";
import Text from "../components/Text";
import Modal from "../components/Modal";
import BotSelector from "../components/BotSelector";
import CurrencyInput from "../components/CurrencyInput";
import { Badge, type BadgeTone } from "../components/Badge";
import { Pagination } from "../components/Pagination";
import { SearchInput } from "../components/SearchInput";
import { EmptyState } from "../components/EmptyState";
import { SkeletonAuditRow } from "../components/Skeleton";
import ItemStackView from "../components/ItemStackView";
import AhListingName from "../components/AhListingName";
import Sparkline from "../components/Sparkline";
import { formatInteger } from "../services/numberFormat";
import { formatDateTime } from "../services/dateFormat";
import { useActiveBot } from "../state/activeBot";
import { listingLore, remainingMs, formatRemaining, toStack, statusOf, type ListingStatus } from "../services/ahListing";
import type { AhListingDto, AhSnapshotDto, AhStatsDto } from "../api/generated/schemas";
import styles from "./AhBrowse.module.css";

const PAGE_SIZE = 24;

// An ended auction says what became of it, unless it left the panel before a history scan picked it up.
function badgeTone(status: ListingStatus, outcome: string): BadgeTone {
  switch (status) {
    case "active": return "success";
    case "ending": return "warning";
  }
  switch (outcome) {
    case "Sold": return "success";
    case "Withdrawn": return "warning";
    default: return "neutral";
  }
}

function badgeKey(status: ListingStatus, outcome: string): string {
  switch (status) {
    case "active": return "AH_STATUS_ACTIVE";
    case "ending": return "AH_STATUS_ENDING";
  }
  switch (outcome) {
    case "Sold": return "AH_OUTCOME_SOLD";
    case "Unsold": return "AH_OUTCOME_UNSOLD";
    case "Withdrawn": return "AH_OUTCOME_WITHDRAWN";
    default: return "AH_STATUS_EXPIRED";
  }
}

export default function AhBrowse() {
  const api = useApi();
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [search, setSearch] = createSignal("");
  const [status, setStatus] = createSignal("active");
  const [binOnly, setBinOnly] = createSignal(false);
  const [sortBy, setSortBy] = createSignal("expiry");
  const [page, setPage] = createSignal(0);
  const [selected, setSelected] = createSignal<AhListingDto | null>(null);
  const [bidFor, setBidFor] = createSignal<AhListingDto | null>(null);
  const [stats, setStats] = createSignal<AhStatsDto | null>(null);

  // Countdowns tick locally; the listing data itself only changes when a bot scans.
  const [now, setNow] = createSignal(Date.now());
  onMount(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    onCleanup(() => clearInterval(timer));
  });

  // The stream carries the live numbers, but an EventSource cannot be intercepted by the preview
  // fake backend and a proxy hiccup would leave the tiles blank, so one plain read seeds them.
  onMount(async () => {
    const res = await api.ah.getStats();
    if (isOk(res.data) && res.data.value) setStats(res.data.value);
  });

  onMount(() => {
    const stream = new EventSource("/api/Ah/stream", { withCredentials: true });
    stream.onmessage = (event) => {
      try { setStats(JSON.parse(event.data) as AhStatsDto); } catch { /* a partial frame resolves on the next tick */ }
    };
    onCleanup(() => stream.close());
  });

  const [listings, { refetch }] = createResource(
    () => ({ search: search(), status: status(), binOnly: binOnly(), sortBy: sortBy(), page: page() }),
    async (query) => {
      const res = await api.ah.getListings({
        search: query.search || undefined,
        status: query.status || undefined,
        binOnly: query.binOnly || undefined,
        sortBy: query.sortBy,
        page: query.page,
        pageSize: PAGE_SIZE,
      });
      if (!isOk(res.data)) return { items: [] as AhListingDto[], total: 0 };
      return res.data.value!;
    }
  );

  // A push notification deep-links to one listing, so the drawer opens on that id.
  onMount(async () => {
    const wanted = searchParams.listing;
    if (typeof wanted !== "string" || !wanted) return;
    const res = await api.ah.getListing(wanted);
    if (isOk(res.data) && res.data.value) setSelected(res.data.value);
    setSearchParams({ listing: undefined });
  });

  const totalPages = createMemo(() => Math.max(1, Math.ceil((listings()?.total ?? 0) / PAGE_SIZE)));

  const watchThis = (listing: AhListingDto) => {
    navigate(`/ah/watches?fromListing=${listing.id}`);
  };

  return (
    <div class={styles.container}>
      <div class={styles.headerRow}>
        <div class={styles.titleBlock}>
          <h1><Text val="AH_TITLE" /></h1>
          <p class={styles.subtitle}><Text val="AH_SUBTITLE" /></p>
        </div>
        <div class={styles.headerActions}>
          <button class="btn" onClick={() => navigate("/ah/watches")}>
            <i class="fa-solid fa-bell" /> <Text val="AH_NAV_WATCHES" />
          </button>
          <button class="btn btn-primary" onClick={() => refetch()}>
            <i class="fa-solid fa-rotate" /> <Text val="AH_REFRESH" />
          </button>
        </div>
      </div>

      <div class={styles.statRow}>
        <StatTile icon="fa-gavel" tone="success" labelKey="AH_STAT_ACTIVE" value={stats()?.activeCount} />
        <StatTile icon="fa-hourglass-half" tone="warning" labelKey="AH_STAT_ENDING" value={stats()?.expiringSoonCount} />
        <StatTile icon="fa-layer-group" tone="info" labelKey="AH_STAT_TOTAL" value={stats()?.totalCount} />
        <div class={`fcard ${styles.statTile}`}>
          <div class={`${styles.statIcon} ${styles.toneNeutral}`}><i class="fa-solid fa-clock-rotate-left" /></div>
          <div class={styles.statText}>
            <span class={styles.statLabel}><Text val="AH_STAT_LAST_SCAN" /></span>
            <span class={styles.statValueSm}>{stats()?.lastScanAt ? formatDateTime(stats()!.lastScanAt!) : "--"}</span>
          </div>
        </div>
      </div>

      <div class={`fcard ${styles.toolbar}`}>
        <div class={styles.searchWrap}>
          <SearchInput
            value={search()}
            onInput={(value) => { setSearch(value); setPage(0); }}
            placeholder={t("AH_SEARCH_PLACEHOLDER")}
            ariaLabel={t("AH_SEARCH_PLACEHOLDER")}
          />
        </div>
        <select value={status()} onChange={(e) => { setStatus(e.currentTarget.value); setPage(0); }}>
          <option value="">{t("AH_FILTER_ALL")}</option>
          <option value="active">{t("AH_STATUS_ACTIVE")}</option>
          <option value="expired">{t("AH_STATUS_EXPIRED")}</option>
        </select>
        <select value={sortBy()} onChange={(e) => { setSortBy(e.currentTarget.value); setPage(0); }}>
          <option value="expiry">{t("AH_SORT_EXPIRY")}</option>
          <option value="bid">{t("AH_SORT_BID")}</option>
          <option value="firstSeen">{t("AH_SORT_NEWEST")}</option>
        </select>
        <label class={styles.toggle}>
          <input type="checkbox" checked={binOnly()} onChange={(e) => { setBinOnly(e.currentTarget.checked); setPage(0); }} />
          <Text val="AH_FILTER_BIN_ONLY" />
        </label>
      </div>

      <Show
        when={!listings.loading}
        fallback={<div class={styles.grid}><For each={Array(6).fill(0)}>{() => <SkeletonAuditRow />}</For></div>}
      >
        <Show
          when={(listings()?.items.length ?? 0) > 0}
          fallback={<EmptyState illustration="jobs" title={t("AH_EMPTY_TITLE")} hint={t("AH_EMPTY_HINT")} />}
        >
          <div class={styles.grid}>
            <For each={listings()!.items}>
              {(listing) => (
                <ListingCard
                  listing={listing}
                  now={now()}
                  onOpen={() => setSelected(listing)}
                  onWatch={() => watchThis(listing)}
                  onBid={() => setBidFor(listing)}
                />
              )}
            </For>
          </div>
          <Pagination page={page()} totalPages={totalPages()} onChange={setPage} />
        </Show>
      </Show>

      <ListingDetail listing={selected()} now={now()} onClose={() => setSelected(null)} />
      <BidDialog
        listing={bidFor()}
        onClose={() => setBidFor(null)}
        onPlaced={(message) => { toast?.success(message); setBidFor(null); }}
        onFailed={(message) => toast?.error(message)}
      />
    </div>
  );
}

function StatTile(props: { icon: string; tone: string; labelKey: string; value?: number }) {
  const toneClass = () => styles[`tone${props.tone[0].toUpperCase()}${props.tone.slice(1)}`] ?? "";
  return (
    <div class={`fcard ${styles.statTile}`}>
      <div class={`${styles.statIcon} ${toneClass()}`}><i class={`fa-solid ${props.icon}`} /></div>
      <div class={styles.statText}>
        <span class={styles.statLabel}><Text val={props.labelKey} /></span>
        <span class={styles.statValue}>{props.value != null ? formatInteger(props.value) : "--"}</span>
      </div>
    </div>
  );
}

function ListingCard(props: {
  listing: AhListingDto;
  now: number;
  onOpen: () => void;
  onWatch: () => void;
  onBid: () => void;
}) {
  const state = () => statusOf(props.listing, props.now);
  const remaining = () => remainingMs(props.listing, props.now);
  const { t } = useTranslation();

  return (
    <div class={`fcard ${styles.card} ${state() === "ending" ? styles.cardHot : ""}`}>
      <button type="button" class={styles.cardMain} onClick={props.onOpen}>
        <ItemStackView stack={toStack(props.listing)} size={44} showTooltip loreOverride={listingLore(props.listing.snbt)} />
        <div class={styles.cardText}>
          <span class={styles.cardName}>
            <AhListingName listing={props.listing} />
          </span>
        </div>
      </button>

      <div class={styles.cardPrices}>
        <div class={styles.priceCol}>
          <span class={styles.priceLabel}><Text val="AH_BID" /></span>
          <span class={styles.priceVal}>{formatInteger(props.listing.currentBid ?? 0)}$</span>
        </div>
        <Show when={props.listing.buyItNow != null}>
          <div class={styles.priceCol}>
            <span class={styles.priceLabel}><Text val="AH_BIN" /></span>
            <span class={`${styles.priceVal} ${styles.binVal}`}>{formatInteger(props.listing.buyItNow!)}$</span>
          </div>
        </Show>
        <Show when={props.listing.soldFor != null}>
          <div class={styles.priceCol}>
            <span class={styles.priceLabel}><Text val="AH_SOLD_FOR" /></span>
            <span class={`${styles.priceVal} ${styles.binVal}`}>{formatInteger(props.listing.soldFor!)}$</span>
          </div>
        </Show>
      </div>

      <Show when={props.listing.seller}>
        {(seller) => (
          <div class={styles.seller}>
            <i class="fa-solid fa-user" /> <Text val="AH_SELLER" /> {seller()}
          </div>
        )}
      </Show>

      <div class={styles.cardFooter}>
        <Show
          when={state() !== "expired"}
          fallback={<span class={styles.endedAt}><Text val="AH_ENDED" /> {formatDateTime(props.listing.expiredAt!)}</span>}
        >
          <span class={`${styles.countdown} ${state() === "ending" ? styles.countdownHot : ""}`}>
            <i class="fa-solid fa-clock" /> {remaining() > 0 ? formatRemaining(remaining()) : t("AH_ENDING_NOW")}
          </span>
        </Show>
        <Badge tone={badgeTone(state(), props.listing.outcome)} pill pulse={state() === "ending"}>
          <Text val={badgeKey(state(), props.listing.outcome)} />
        </Badge>
      </div>

      <div class={styles.cardActions}>
        <button class="btn" onClick={props.onWatch} title={t("AH_WATCH_THIS_HINT")}>
          <i class="fa-solid fa-bell" /> <Text val="AH_WATCH_THIS" />
        </button>
        <button class="btn btn-primary" disabled={state() === "expired"} onClick={props.onBid}>
          <i class="fa-solid fa-gavel" /> <Text val="AH_BID_ACTION" />
        </button>
      </div>
    </div>
  );
}

function ListingDetail(props: { listing: AhListingDto | null; now: number; onClose: () => void }) {
  const api = useApi();
  const { t } = useTranslation();

  const [snapshots] = createResource(
    () => props.listing?.id,
    async (id) => {
      const res = await api.ah.getSnapshots(id);
      if (!isOk(res.data)) return [] as AhSnapshotDto[];
      return (res.data.value as { snapshots?: AhSnapshotDto[] }).snapshots ?? [];
    }
  );

  const bidSeries = createMemo(() => (snapshots() ?? []).map(snapshot => snapshot.currentBid ?? 0));

  return (
    <Modal open={props.listing != null} onClose={props.onClose} title={t("AH_DETAIL_SUBTITLE")}>
      <Show when={props.listing}>
        {(listing) => (
          <div class={styles.detail}>
            <ItemStackView stack={toStack(listing())} size={56} loreOverride={listingLore(listing().snbt)} inlineTooltip />

            <div class={styles.detailGrid}>
              <Field labelKey="AH_BID" value={`${formatInteger(listing().currentBid ?? 0)}$`} />
              <Field labelKey="AH_STARTING_BID" value={`${formatInteger(listing().startingBid ?? 0)}$`} />
              <Show when={listing().buyItNow != null}>
                <Field labelKey="AH_BIN" value={`${formatInteger(listing().buyItNow!)}$`} />
              </Show>
              <Show when={listing().soldFor != null}>
                <Field labelKey="AH_SOLD_FOR" value={`${formatInteger(listing().soldFor!)}$`} />
              </Show>
              <Field labelKey="AH_SCAN_COUNT" value={formatInteger(listing().scanCount)} />
              <Field labelKey="AH_FIRST_SEEN" value={formatDateTime(listing().firstSeenAt!)} />
              <Field labelKey="AH_EST_EXPIRY" value={formatDateTime(listing().estimatedExpiryUtc!)} />
              <Show when={listing().expiredAt}>
                <Field labelKey="AH_OUTCOME" value={t(badgeKey(statusOf(listing(), props.now), listing().outcome))} />
              </Show>
              <Show when={listing().seller}>
                <Field labelKey="AH_SELLER" value={listing().seller!} />
              </Show>
            </div>

            <Show when={bidSeries().length > 1}>
              <div class={styles.detailChart}>
                <span class={styles.detailLabel}><Text val="AH_BID_HISTORY" /></span>
                <Sparkline data={bidSeries()} width={520} height={80} />
              </div>
            </Show>
          </div>
        )}
      </Show>
    </Modal>
  );
}

function Field(props: { labelKey: string; value: string }) {
  return (
    <div class={styles.field}>
      <span class={styles.fieldLabel}><Text val={props.labelKey} /></span>
      <span class={styles.fieldValue}>{props.value}</span>
    </div>
  );
}

function BidDialog(props: {
  listing: AhListingDto | null;
  onClose: () => void;
  onPlaced: (message: string) => void;
  onFailed: (message: string) => void;
}) {
  const api = useApi();
  const { t } = useTranslation();
  const { botId: activeBotId } = useActiveBot();
  const [botId, setBotId] = createSignal<string | null>(null);
  const [amount, setAmount] = createSignal<number | "">("");
  const [busy, setBusy] = createSignal(false);

  const minimum = () => (props.listing?.currentBid ?? 0) + 1;
  const belowMinimum = () => {
    const typed = amount();
    return typed === "" || typed < minimum();
  };

  createEffect(on(() => props.listing, (listing) => setAmount(listing ? minimum() : "")));

  const place = async () => {
    const listing = props.listing;
    const bot = botId();
    const typed = amount();
    if (!listing || !bot || typed === "") return;

    setBusy(true);
    try {
      const res = await api.ah.placeBid({ botId: bot, auctionId: listing.id, amount: typed });
      if (!isOk(res.data) || !res.data.value?.queued) {
        props.onFailed(res.data?.value?.message ?? t("AH_BID_FAILED"));
        return;
      }
      props.onPlaced(t("AH_BID_QUEUED"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={props.listing != null} onClose={props.onClose} title={t("AH_BID_TITLE")}>
      <Show when={props.listing}>
        {(listing) => (
          <div class={styles.bidForm}>
            <p class={styles.bidLead}>
              <Text val="AH_BID_LEAD" /> <strong><AhListingName listing={listing()} /></strong>
            </p>
            <label class={styles.bidField}>
              <span><Text val="AH_BID_BOT" /></span>
              <BotSelector storageKey="ah.bid.bot" defaultId={activeBotId() ?? undefined} onSelect={(id) => setBotId(id)} />
            </label>
            <label class={styles.bidField}>
              <span><Text val="AH_BID_AMOUNT" /></span>
              <CurrencyInput
                value={amount()}
                onChange={setAmount}
                maxDecimals={0}
                aria-label={t("AH_BID_AMOUNT")}
              />
              <small classList={{ [styles.bidBelowMinimum]: amount() !== "" && belowMinimum() }}>
                <Text val="AH_BID_CURRENT" /> {formatInteger(listing().currentBid ?? 0)}$, {t("AH_BID_MINIMUM", formatInteger(minimum()))}
              </small>
            </label>
            <div class={styles.bidActions}>
              <button class="btn" onClick={props.onClose}><Text val="CANCEL" /></button>
              <button
                class="btn btn-primary"
                disabled={busy() || !botId() || belowMinimum()}
                onClick={place}
              >
                <i class="fa-solid fa-gavel" /> <Text val="AH_BID_CONFIRM" />
              </button>
            </div>
          </div>
        )}
      </Show>
    </Modal>
  );
}
